/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
/**
 * ProjectUtils.ts
 * ---------------
 * Contains utility functions for Project state management and business logic.
 *
 * - Provides helper methods for working with nodes and edges, connections, and other data transformations.
 * - Contains functions for loading data from APIs, performing mappings, and propagating results within the project state.
 * - Used by the ProjectReducer and other high-level state functions to keep business logic modular and reusable.
 *
 * This file is the primary location for procedural utilities that operate on project state or perform common operations.
 */
import { AxiosInstance } from "axios";
import { Connection } from "@xyflow/react";
import { addEdge } from "@xyflow/react";
import {
  Edge,
  extractDataType,
  FileInfoPair,
  Granual,
  Mapping,
  Path,
} from "../Api/DataTransformation";
import {
  NodeDescriptor,
  OutputNodeDescriptor,
  InputNodeDescriptor,
  ComposerNodeDescriptor,
} from "../Api/Api";
import {
  normalizeRawMappingResult,
  isFileInfoPair,
} from "../TransformEngine/TransformNodes/MappingResultUtils";
import {
  findNodeEntry,
  getHandleFromString,
  getInputNodeAndPathsForComposer,
  modifyComposerNodeData,
  modifyOutputNodeData,
  PARENT_HANDLE_ID,
  ReactFlowEdge,
  ReactFlowNode,
  TransformEngineNodeData,
  TransformEngineNodeEntry,
} from "../TransformEngine/TransformNodes/Util/TransformEngineDataUtils";
import { CancelToken } from "../Informedb/CancelToken";
import {
  lastPathElement,
  nodeFromDescriptor,
} from "../TransformEngine/NodeDescriptorUtil";
import { loggedNotify, Component } from "../Shared/Notify";
import { ProjectState } from "./ProjectModels";
import { ProjectStorableState, AsyncProjectData } from "./ProjectModels";
import { FileType } from "../Api/Api";
import { Endpoints } from "../Utils/Common";
import { isolateProject } from "./ProjectPersistence";
import { NODE_TYPES } from "../Shared/TransformNodeTypes";
import { createUUID } from "../Utils/Types";
import { useState, useEffect } from "react";

export function nodeInitialPosition(): ReactFlowNode["position"] {
  // Set the initial position, nodes will be repositioned after
  // creation for final spacing for clean layout
  const baseXPos = 0;
  const baseYPos = 20;

  return {
    x: baseXPos,
    y: baseYPos,
  };
}

export function addNodeFromDescriptor(
  descriptor: NodeDescriptor,
  existingNodes: ReactFlowNode[],
  position?: { x: number; y: number }
): ReactFlowNode[] {
  const dataID = generateUniqueDataID(descriptor.displayName);

  const rfNode = nodeFromDescriptor(
    dataID,
    descriptor,
    position ?? nodeInitialPosition() // Use given position or auto-calc
  );

  return existingNodes.concat(rfNode);
}

function generateUniqueDataID(displayName: string): string {
  const nodeId = `${displayName}-${createUUID()}`;

  return nodeId;
}

export function doConnection(
  existingEdges: ReactFlowEdge[],
  params: Connection | ReactFlowEdge
): ReactFlowEdge[] {
  return addEdge(params, existingEdges);
}

export async function loadProjects(
  axiosContext: AxiosInstance,
  _cancel: CancelToken
): Promise<ProjectStorableState[]> {
  try {
    const response = await axiosContext.get(Endpoints.GetProjects);

    if (response.status == 200) {
      return response.data;
    } else {
      loggedNotify.negative(
        axiosContext,
        Component.TRANSFORM_ENGINE,
        `HTTP status ${response.status} - Unable to get projects`
      );
      return [];
    }
  } catch (error) {
    loggedNotify.negative(
      axiosContext,
      Component.TRANSFORM_ENGINE,
      `Unable to get projects: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
    return [];
  }
}

export async function loadProjectData(
  axiosContext: AxiosInstance,
  cancel: CancelToken
): Promise<AsyncProjectData> {
  const projects = await loadProjects(axiosContext, cancel);
  return { projects };
}

// Extracts some data from the Transform Engine state to be used in the data transformation mapping.
export function toMappingEdge(
  existingNodes: ReactFlowNode[],
  edge: ReactFlowEdge
): Edge {
  const source = existingNodes.find((node) => edge.source == node.data.dataID);
  const target = existingNodes.find((node) => edge.target == node.data.dataID);

  if (source && target) {
    const sourceHandle = getHandleFromString(source.data, edge.sourceHandle);
    const targetHandle = getHandleFromString(target.data, edge.targetHandle);

    if (sourceHandle && targetHandle) {
      const sourcePathEndpoint = lastPathElement(sourceHandle.path);
      const targetPathEndpoint = lastPathElement(targetHandle.path);

      const sourceEntry = findNodeEntry(source.data, sourceHandle.entryId);
      const targetEntry = findNodeEntry(target.data, targetHandle.entryId);

      // Build contextual label with parent message name
      const sourceLabel = buildContextualLabel(
        sourceEntry,
        sourceHandle.path,
        sourcePathEndpoint.byString
      );
      const targetLabel = buildContextualLabel(
        targetEntry,
        targetHandle.path,
        targetPathEndpoint.byString
      );

      const mappingEdge: Edge = {
        sourceID: source.data.dataID,
        sourcePath: sourceHandle.path,
        sourceLabel,
        targetID: target.data.dataID,
        targetPath: targetHandle.path,
        targetLabel,
      };
      return mappingEdge;
    } else {
      return undefined;
    }
  }

  return undefined;
}

/**
 * Build a contextual label that includes parent message context.
 * We'll be able to wrap a primitive in its parent.
 * For primitives: "ParentMessage fieldType fieldName"
 * For messages: "message MessageName"
 * For nested messages: "ParentMessage.NestedMessage"
 */
function buildContextualLabel(
  entry: TransformEngineNodeEntry | undefined,
  path: Path,
  fallbackLabel: string
): string {
  const pathEntries = path.pathEntries;

  // Early return if no path
  if (!pathEntries || pathEntries.length === 0) {
    return entry?.label ?? fallbackLabel;
  }

  // Get the leaf label with proper null checking
  let leafLabel: string | undefined;

  if (entry?.label) {
    leafLabel = entry.label;
  } else if (
    pathEntries.length > 0 &&
    pathEntries[pathEntries.length - 1]?.byString
  ) {
    leafLabel = pathEntries[pathEntries.length - 1].byString;
  } else {
    leafLabel = fallbackLabel;
  }

  // Final safety check
  if (!leafLabel) {
    return fallbackLabel;
  }

  // Check if this is a message (will start with "message ")
  if (leafLabel.startsWith("message ")) {
    // For messages, include the full path to show nesting
    // e.g., "message Order.OrderItem" for nested messages
    if (pathEntries.length > 1) {
      const parentMessages = pathEntries
        .slice(0, -1)
        .filter((pathEntry) => pathEntry?.byString) // Filter out undefined
        .map((pathEntry) => pathEntry.byString.replace("message ", ""))
        .filter((name) => name.length > 0) // Filter empty strings
        .join(".");

      if (parentMessages.length > 0) {
        const messageName = leafLabel.replace("message ", "");
        return `message ${parentMessages}.${messageName}`;
      }
    }
    return leafLabel; // Top-level message, no parent
  }

  // For primitive fields, find the parent message
  // Walk up the path to find the first "message" entry
  let parentMessageName: string | null = null;
  // eslint-disable-next-line no-loops/no-loops
  for (let i = pathEntries.length - 2; i >= 0; i--) {
    const pathEntry = pathEntries[i];
    if (pathEntry?.byString && pathEntry.byString.startsWith("message ")) {
      parentMessageName = pathEntry.byString.replace("message ", "");
      break;
    }
  }

  // If we found a parent message, prepend it
  if (parentMessageName) {
    return `${parentMessageName} ${leafLabel}`;
  }

  // No parent message found, return as-is
  return leafLabel;
}

export async function saveMapping(
  state: ProjectState,
  cancel: CancelToken
): Promise<ProjectStorableState[]> {
  try {
    const composerNodes = state.rfNodes.filter(
      (node) => node.type == NODE_TYPES.composer
    );
    const currentComposers = composerNodes.map(
      (node) => node.data.descriptor as ComposerNodeDescriptor
    );

    const storable = {
      ...state,
      rfNodes: state.rfNodes,
      rfEdges: state.rfEdges,
      inputNodeDescriptors: state.inputNodeDescriptors,
      composerNodes: currentComposers,
    };

    const postResponse = await state.axiosContext.post(
      Endpoints.AddProject,
      isolateProject(storable)
    );
    if (typeof postResponse.data === "string") {
      if (typeof postResponse.data === "string") {
        loggedNotify.positive(
          state.axiosContext,
          Component.TRANSFORM_ENGINE,
          postResponse.data
        );
      }
    } else {
      const data = postResponse.data;
      if (data.projectID == state.projectID) {
        loggedNotify.positive(
          state.axiosContext,
          Component.TRANSFORM_ENGINE,
          "Mapping saved successfully"
        );
      } else {
        loggedNotify.negative(
          state.axiosContext,
          Component.TRANSFORM_ENGINE,
          "Unable to save mapping" + (data.message ? ": " + data.message : "")
        );
      }
    }

    return await loadProjects(state.axiosContext, cancel);
  } catch (error) {
    loggedNotify.negative(
      state.axiosContext,
      Component.TRANSFORM_ENGINE,
      `Unable to save mapping: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
    return [];
  }
}

export async function saveMappingAsNew(
  state: ProjectState,
  newName: string,
  cancel: CancelToken
): Promise<ProjectStorableState[]> {
  try {
    const storable = {
      ...state,
      projectName: newName,
      rfNodes: state.rfNodes,
      rfEdges: state.rfEdges,
      inputNodeDescriptors: state.inputNodeDescriptors,
    };

    // Remove projectID from the object so backend generates a new one
    const projectToSave = isolateProject(storable);
    delete projectToSave.projectID; // Backend will generate new UUID

    const postResponse = await state.axiosContext.post(
      Endpoints.AddProject,
      projectToSave
    );

    if (typeof postResponse.data === "string") {
      if (typeof postResponse.data === "string") {
        loggedNotify.positive(
          state.axiosContext,
          Component.TRANSFORM_ENGINE,
          postResponse.data
        );
      }
    } else {
      const data = postResponse.data;
      // Don't check projectID match since we created a new one
      loggedNotify.positive(
        state.axiosContext,
        Component.TRANSFORM_ENGINE,
        `Mapping saved as "${newName}"`
      );

      // Store the new project ID so we can switch to it
      if (data.projectID) {
        sessionStorage.setItem("lastCreatedProjectID", data.projectID);
      }
    }

    return await loadProjects(state.axiosContext, cancel);
  } catch (error) {
    loggedNotify.negative(
      state.axiosContext,
      Component.TRANSFORM_ENGINE,
      `Unable to save mapping as new: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
    return [];
  }
}

/**
 * Now supports multiple file IDs via associatedFileIds array
 */
export async function performMapping(
  state: ProjectState,
  transformId: string,
  _cancel: CancelToken,
  granuals?: Granual[],
  associatedFileId?: string,
  silent?: boolean
): Promise<FileInfoPair | boolean | string> {
  try {
    // Build file IDs array from granuals if they have sourceFileId
    const fileIdsFromGranuals = granuals
      ?.map((granual) => granual.sourceFileId)
      .filter((id): id is string => !!id);

    const uniqueFileIds = fileIdsFromGranuals
      ? Array.from(new Set(fileIdsFromGranuals))
      : undefined;

    const mapping: Mapping = {
      transformId,
      associatedFileId, // Keep for backward compatibility
      associatedFileIds: uniqueFileIds, //  Multiple file IDs
      granuals: granuals,
    };

    const postResponse = await state.axiosContext.post(
      Endpoints.PerformMapping,
      mapping,
      { validateStatus: () => true }
    );

    if (postResponse.data != null) {
      const data = postResponse.data;

      if (data.status === "success") {
        if (!silent) {
          loggedNotify.positive(
            state.axiosContext,
            Component.TRANSFORM_ENGINE,
            "Data transformation successful — ready to link to an output node for download"
          );
        }
        return data.data as FileInfoPair;
      } else if (data.status === "fail") {
        loggedNotify.negative(
          state.axiosContext,
          Component.TRANSFORM_ENGINE,
          "Transformation failed" +
            (data.error && data.error.length > 0 ? ": " + data.error : "")
        );
        return false;
      }
    } else {
      loggedNotify.negative(
        state.axiosContext,
        Component.TRANSFORM_ENGINE,
        "Transformation failed: No response received"
      );
      return false;
    }
  } catch (error) {
    loggedNotify.negative(
      state.axiosContext,
      Component.TRANSFORM_ENGINE,
      `Unable to perform mapping: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
    return false;
  }
}

export function propagateTransformToOutputsInReducer(
  rfNodes: ReactFlowNode[],
  rfEdges: ReactFlowEdge[],
  transformNodeID: string,
  axiosContext: AxiosInstance,
  silent?: boolean
): ReactFlowNode[] {
  let updatedNodes = rfNodes;
  const transformNode = rfNodes.find(
    (node) => node.data.dataID === transformNodeID
  );
  if (!transformNode) return rfNodes;

  const normalizedTransformResult = normalizeRawMappingResult(
    transformNode.data.mappingResult
  );

  rfEdges.forEach((edge) => {
    if (edge.source === transformNode.data.dataID) {
      const targetNode = updatedNodes.find(
        (node) => node.data.dataID === edge.target
      );

      if (!targetNode) return;

      const isOutputNode = targetNode.type === NODE_TYPES.output;
      const isComposerNode = targetNode.type === NODE_TYPES.composer;

      // Handle OUTPUT nodes
      if (isOutputNode) {
        const previousDescriptor = targetNode.data
          .descriptor as OutputNodeDescriptor;

        let isTransformReadyOut = false;
        let fileTypeOut = FileType.UNDETECTED;
        if (isFileInfoPair(normalizedTransformResult)) {
          isTransformReadyOut = true;
          fileTypeOut = normalizedTransformResult.fileType;
        }

        const modifiedOutputNodeData: TransformEngineNodeData = {
          ...targetNode.data,
          mappingResult: normalizedTransformResult,
        };

        const modifiedOutputNodeDescriptor: OutputNodeDescriptor = {
          ...(targetNode.data.descriptor as OutputNodeDescriptor),
          isTransformReady: isTransformReadyOut,
          type: fileTypeOut,
        };

        updatedNodes = modifyOutputNodeData(
          updatedNodes,
          modifiedOutputNodeData,
          modifiedOutputNodeDescriptor
        );

        const becomingReady =
          isTransformReadyOut && !previousDescriptor.isTransformReady;
        if (becomingReady && !silent) {
          loggedNotify.positive(
            axiosContext,
            Component.TRANSFORM_ENGINE,
            "Output node connection successful. Output file ready for download"
          );
        }
      }

      // Handle COMPOSER nodes
      if (isComposerNode) {
        let isTransformReadyComposer = false;
        let fileTypeComposer = FileType.UNDETECTED;
        if (isFileInfoPair(normalizedTransformResult)) {
          isTransformReadyComposer = true;
          fileTypeComposer = normalizedTransformResult.fileType;
        }

        // Get the input node to trace back to the original source
        const inputNode = rfNodes.find(
          (node) => node.data.dataID === edge.source
        );

        // Get the composer data using the helper function
        const { pathGroups, sourceInputNode } = getInputNodeAndPathsForComposer(
          rfEdges,
          updatedNodes,
          inputNode
        );

        const modifiedComposerNodeData: TransformEngineNodeData = {
          ...targetNode.data,
          mappingResult: normalizedTransformResult,
        };

        const modifiedComposerNodeDescriptor: ComposerNodeDescriptor = {
          ...(targetNode.data.descriptor as ComposerNodeDescriptor),
          type: fileTypeComposer,
          sourceInputNode: sourceInputNode,
          composerData: pathGroups,
        };

        updatedNodes = modifyComposerNodeData(
          updatedNodes,
          modifiedComposerNodeData,
          modifiedComposerNodeDescriptor
        );

        if (isTransformReadyComposer && !silent) {
          loggedNotify.positive(
            axiosContext,
            Component.TRANSFORM_ENGINE,
            "Composer node connection successful. Output file ready for download"
          );
        }
      }
    }
  });

  return updatedNodes;
}

export function isInputNodeDescriptor(
  descriptor: NodeDescriptor
): descriptor is InputNodeDescriptor {
  const inputNodeDescriptor = descriptor as InputNodeDescriptor;

  return (
    !!inputNodeDescriptor &&
    typeof inputNodeDescriptor.associatedFileId === "string" &&
    typeof inputNodeDescriptor.type === "string"
  );
}

interface TransformNodeInputInfo {
  fileId: string;
  granuals?: Granual[];
  mappingEdge: Edge;
  incomingEdge: ReactFlowEdge;
  sourceNode: ReactFlowNode;
}

export function getTransformNodeInputInfo(
  transformNode: ReactFlowNode,
  rfEdges: ReactFlowEdge[],
  rfNodes: ReactFlowNode[]
): TransformNodeInputInfo | null {
  const incomingEdge = rfEdges.find(
    (edge) => edge.target === transformNode.data.dataID
  );
  if (!incomingEdge) return null;

  const sourceNode = rfNodes.find(
    (node) => incomingEdge.source === node.data.dataID
  );
  if (!sourceNode) return null;

  const fileId = (sourceNode.data.descriptor as InputNodeDescriptor)
    .associatedFileId;
  if (!fileId) return null;

  const mappingEdge = toMappingEdge(rfNodes, incomingEdge);
  if (!mappingEdge) return null;

  // Use multi-source function
  const granualsData = buildGranualsFromAllConnectedInputs(
    rfEdges,
    rfNodes,
    transformNode.data.dataID
  );

  return {
    fileId,
    granuals: granualsData.granuals.length > 0 ? granualsData.granuals : null,
    mappingEdge,
    incomingEdge,
    sourceNode,
  };
}

/**
 * Old function that only collected granuals from a single source
 * Kept for backward compatibility but new code should use buildGranualsFromAllConnectedInputs
 */
export function buildGranualsFromNonParentEdges(
  edges: ReactFlowEdge[],
  nodes: ReactFlowNode[],
  targetTransformNodeID: string,
  sourceNodeID: string
): Granual[] {
  // Filter to only edges from this source TO this specific transform node
  const relevantEdges = edges.filter(
    (edge) =>
      edge.target === targetTransformNodeID &&
      edge.source === sourceNodeID &&
      edge.sourceHandle !== PARENT_HANDLE_ID // Exclude parent handle
  );

  const currentEdges = relevantEdges
    .map((edge) => toMappingEdge(nodes, edge))
    .filter((edge): edge is Edge => !!edge);

  // Get the file ID for this source
  const sourceNode = nodes.find((node) => node.data.dataID === sourceNodeID);
  const fileId = sourceNode
    ? (sourceNode.data.descriptor as InputNodeDescriptor)?.associatedFileId
    : undefined;

  const granuals: Granual[] = currentEdges.map((edge) => ({
    id: edge.sourceID,
    label: edge.sourceLabel,
    dataType: extractDataType(edge.sourceLabel),
    sourceFileId: fileId, // Tag with source file
  }));
  return granuals;
}

/**
 * Collects granuals from ALL input nodes connected to a transform node
 * This enables combining data from multiple input files in a single transform
 */
export function buildGranualsFromAllConnectedInputs(
  edges: ReactFlowEdge[],
  nodes: ReactFlowNode[],
  targetTransformNodeID: string
): { granuals: Granual[]; fileIds: string[] } {
  // Find ALL edges targeting this transform node (excluding parent handles)
  const relevantEdges = edges.filter(
    (edge) =>
      edge.target === targetTransformNodeID &&
      edge.sourceHandle !== PARENT_HANDLE_ID
  );

  // Group edges by source node to track which file each granual comes from
  const edgesBySource = new Map<string, ReactFlowEdge[]>();
  relevantEdges.forEach((edge) => {
    if (!edgesBySource.has(edge.source)) {
      edgesBySource.set(edge.source, []);
    }
    (edgesBySource.get(edge.source) as ReactFlowEdge[]).push(edge);
  });

  const allGranuals: Granual[] = [];
  const allFileIds: string[] = [];

  // Process each source node's edges
  edgesBySource.forEach((sourceEdges, sourceNodeID) => {
    const sourceNode = nodes.find((node) => node.data.dataID === sourceNodeID);
    if (!sourceNode || sourceNode.type !== NODE_TYPES.input) return;

    const fileId = (sourceNode.data.descriptor as InputNodeDescriptor)
      ?.associatedFileId;
    if (!fileId) return;

    allFileIds.push(fileId);

    // Convert edges to granuals with source file ID
    const mappingEdges = sourceEdges
      .map((edge) => toMappingEdge(nodes, edge))
      .filter((edge): edge is Edge => !!edge);

    const granuals: Granual[] = mappingEdges.map((edge) => ({
      id: edge.sourceID,
      label: edge.sourceLabel,
      dataType: extractDataType(edge.sourceLabel),
      sourceFileId: fileId, // Tag each granual with its source file
    }));

    allGranuals.push(...granuals);
  });

  return {
    granuals: allGranuals,
    fileIds: Array.from(new Set(allFileIds)), // Deduplicate file IDs
  };
}

export function propagateToOutputNodes(
  transformNode: ReactFlowNode,
  normalizedResult: any,
  rfEdges: ReactFlowEdge[],
  rfNodes: ReactFlowNode[],
  dispatch: any,
  outputNodesUpdated: Set<string>,
  composerNodesUpdated: Set<string>
): void {
  rfEdges.forEach((edge: ReactFlowEdge) => {
    if (edge.source === transformNode.data.dataID) {
      const targetNode = rfNodes.find(
        (node: ReactFlowNode) => node.data.dataID === edge.target
      );

      // Update base output node data
      if (targetNode && targetNode.type === NODE_TYPES.output) {
        outputNodesUpdated.add(targetNode.data.dataID);

        const normalizedTransformResult =
          normalizeRawMappingResult(normalizedResult);
        const modifiedOutputNodeData: TransformEngineNodeData = {
          ...targetNode.data,
          mappingResult: normalizedTransformResult,
        };

        let isTransformReady = false;
        let outputType = FileType.UNDETECTED;
        if (isFileInfoPair(normalizedTransformResult)) {
          isTransformReady = true;
          outputType = normalizedTransformResult.fileType;
        }

        const modifiedOutputNodeDescriptor: OutputNodeDescriptor = {
          ...(targetNode.data.descriptor as OutputNodeDescriptor),
          isTransformReady,
          type: outputType,
        };

        dispatch([
          "modifyOutputNodeData",
          {
            modifiedNodeData: modifiedOutputNodeData,
            modifiedOutputNodeDescriptor,
          },
        ]);
      }

      // Update composer node data
      if (targetNode && targetNode.type === NODE_TYPES.composer) {
        const existingDescriptor = targetNode.data
          .descriptor as ComposerNodeDescriptor;
        const existingComposerData = existingDescriptor.composerData;

        // If the composer already has saved (e.g. user-reordered) composerData,
        // preserve it rather than overwriting with the recomputed source pathGroups.
        const hasSavedComposerData =
          Array.isArray(existingComposerData) &&
          existingComposerData.length > 0;

        composerNodesUpdated.add(targetNode.data.dataID);

        const normalizedTransformResult =
          normalizeRawMappingResult(normalizedResult);
        const modifiedComposerNodeData: TransformEngineNodeData = {
          ...targetNode.data,
          mappingResult: normalizedTransformResult,
        };

        let isTransformReady = false;
        let outputType = FileType.UNDETECTED;
        if (isFileInfoPair(normalizedTransformResult)) {
          isTransformReady = true;
          outputType = normalizedTransformResult.fileType;
        }

        const getSourceInfo = getInputNodeAndPathsForComposer(
          rfEdges,
          rfNodes,
          transformNode
        );

        const modifiedComposerNodeDescriptor: ComposerNodeDescriptor = {
          ...existingDescriptor,
          type: isTransformReady ? outputType : FileType.UNDETECTED,
          sourceInputNode: getSourceInfo.sourceInputNode,
          composerData: hasSavedComposerData
            ? existingComposerData
            : getSourceInfo.pathGroups,
          isTransformReady,
        };

        dispatch([
          "modifyComposerNodeData",
          {
            modifiedNodeData: modifiedComposerNodeData,
            modifiedComposerNodeDescriptor,
          },
        ]);
      }
    }
  });
}

export function resetUnusedOutputAndComposerNodes(
  rfNodes: ReactFlowNode[],
  outputNodesUpdated: Set<string>,
  composerNodesUpdated: Set<string>,
  sourceNode: InputNodeDescriptor | null,
  dispatch: any
): void {
  rfNodes.forEach((node: any) => {
    const nodeId = node.data.dataID;

    // Reset OUTPUT nodes that weren't updated
    if (node.type === NODE_TYPES.output && !outputNodesUpdated.has(nodeId)) {
      const resetDescriptor: OutputNodeDescriptor = {
        ...(node.data.descriptor as OutputNodeDescriptor),
        isTransformReady: false,
        type: FileType.UNDETECTED,
      };

      dispatch([
        "modifyOutputNodeData",
        {
          modifiedNodeData: {
            ...node.data,
            mappingResult: null,
          },
          modifiedOutputNodeDescriptor: resetDescriptor,
        },
      ]);
    }

    // Reset composer nodes that weren't updated
    if (
      node.type === NODE_TYPES.composer &&
      !composerNodesUpdated.has(nodeId)
    ) {
      const resetDescriptor: ComposerNodeDescriptor = {
        ...(node.data.descriptor as ComposerNodeDescriptor),
        type: FileType.UNDETECTED,
        sourceInputNode: sourceNode,
        composerData: [],
      };

      dispatch([
        "modifyComposerNodeData",
        {
          modifiedNodeData: {
            ...node.data,
            mappingResult: null,
          },
          modifiedComposerNodeDescriptor: resetDescriptor,
        },
      ]);
    }
  });
}

interface WindowSize {
  width: number;
  height: number;
}

export const useWindowSize = (): WindowSize => {
  const [windowSize, setWindowSize] = useState<WindowSize>({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = (): void => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return windowSize;
};
